import { AccountInfo, EventType } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  createContext,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  handleRedirectPromise,
  loginRedirect,
  logoutRedirect,
  msalInstance,
} from "../Api/Common/Auth";
import { fetchAdoUsername } from "../Api/ado.api";
import { BaseErrorMessageBar } from "../Shared/BaseErrorMessageBar";
import SplashScreen from "../Layout/SplashScreen";
import { MessageBarButton } from "@fluentui/react";

interface DataAccessContextValue {
  activeAccount: AccountInfo | null;
  corpAccount: AccountInfo | null;
  includeEudbPortalTelemetry: boolean;
  auth: { success?: boolean; error?: any } | undefined;
  setActiveAccount: (account: AccountInfo) => void;
  hasAdoAccess?: boolean;
  setHasAdoAccess: (hasAccess: boolean) => void;
}

export const eudbTenantIds = new Set([
  "33e01921-4d64-4f8c-a055-5bdaffd5e33d", // AME
  "975f013f-7f24-47e8-a7d3-abc4752bf346", // PME
]);

export const DataAccessContext = createContext<DataAccessContextValue>({
  activeAccount: null,
  corpAccount: null,
  includeEudbPortalTelemetry: false,
  auth: undefined,
  setActiveAccount: null as any,
  hasAdoAccess: undefined,
  setHasAdoAccess: null as any,
});

export const DataAccessContextProvider = (props: PropsWithChildren<{}>) => {
  const { accounts } = useMsal();
  const [activeAccount, setActiveAccountState] = useState(() =>
    msalInstance.getActiveAccount(),
  );
  const [auth, setAuth] = useState<{ success?: boolean; error?: any }>();
  const [iFrameError, setIFrameError] = useState<string | null>(null);
  const [hasAdoAccess, setHasAdoAccess] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    const callbackId = msalInstance.addEventCallback((message) => {
      if (message.eventType === EventType.ACTIVE_ACCOUNT_CHANGED) {
        setActiveAccountState(msalInstance.getActiveAccount());
      }
    });
    return () => {
      callbackId && msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  const setActiveAccount = useCallback((account: AccountInfo) => {
    setActiveAccountState(account);
    msalInstance.setActiveAccount(account);
  }, []);

  useEffect(() => {
    let disposed = false;

    handleRedirectFlow();

    function handleRedirectFlow() {
      handleRedirectPromise().then(() => {
        if (!disposed && !activeAccount) {
          if (msalInstance.getActiveAccount()) {
            setActiveAccountState(msalInstance.getActiveAccount());
          } else {
            const newActiveAccount =
              accounts.find((x) => eudbTenantIds.has(x.tenantId)) ||
              accounts[0];
            if (newActiveAccount) {
              setActiveAccount(newActiveAccount);
            } else {
              loginRedirect().catch((error) => {
                console.error("Login redirect failed: ", error);
                setIFrameError(
                  `Authentication failed: Please attempt to logout and login again`,
                );
              });
            }
          }
        }
      });
    }

    return () => {
      disposed = true;
    };
  }, [activeAccount, accounts, setActiveAccount]);

  const includeEudbPortalTelemetry = useMemo(() => {
    return activeAccount ? eudbTenantIds.has(activeAccount?.tenantId) : false;
  }, [activeAccount]);

  const corpAccount = useMemo(() => {
    return (
      accounts.find(
        (x) => x.tenantId === "72f988bf-86f1-41af-91ab-2d7cd011db47",
      ) || null
    );
  }, [accounts]);

  useEffect(() => {
    let disposed = false;
    if (activeAccount) {
      // Test actual authentication by making a real API call
      // This validates that the user can actually access Azure DevOps
      fetchAdoUsername()
        .then((username) => {
          if (!disposed) {
            if (username) {
              setAuth({ success: true });
            } else {
              setAuth({
                error: new Error(
                  "Authentication failed: Unable to access Azure DevOps",
                ),
              });
            }
          }
        })
        .catch((error) => {
          if (!disposed) {
            console.error("Authentication check failed:", error);
            setAuth({
              error: new Error(`Authentication failed: ${error.message}`),
            });
          }
        });
    } else {
      // No active account means not authenticated
      setAuth(undefined);
    }

    return () => {
      disposed = true;
    };
  }, [activeAccount]);

  return (
    <DataAccessContext.Provider
      value={{
        activeAccount,
        corpAccount,
        includeEudbPortalTelemetry,
        auth,
        setActiveAccount,
        hasAdoAccess,
        setHasAdoAccess,
      }}
    >
      {iFrameError ? (
        <BaseErrorMessageBar error={new Error(iFrameError)} />
      ) : activeAccount && auth ? (
        auth.success ? (
          props.children
        ) : (
          <BaseErrorMessageBar
            error={auth.error}
            actions={<SignOutActionButton activeAccount={activeAccount} />}
          />
        )
      ) : (
        <SplashScreen />
      )}
    </DataAccessContext.Provider>
  );
};

// Custom hook to use the context
export const useDataAccessContext = (): DataAccessContextValue => {
  const context = useContext(DataAccessContext);

  if (context === undefined) {
    throw new Error(
      "useDataAccessContext must be used within an DataAccessProvider",
    );
  }

  return context;
};

const SignOutActionButton = memo((props: { activeAccount: AccountInfo }) => {
  const { activeAccount } = props;
  const onClick = useCallback(() => {
    logoutRedirect(activeAccount);
  }, [activeAccount]);
  return <MessageBarButton onClick={onClick}>Sign out</MessageBarButton>;
});
